import DN from "owp.dn";
import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { TreeNode } from "@gss/common/OrgTree";
import { OrgTree } from "@gss/common/OrgTree";
import { Button } from "@gss/components/Button";
import { useMessages } from "@gss/components/Messages";
import { Select } from "@gss/components/Select";
import { useConst } from "@gss/hooks/useConst";
import type { DimensionValue } from "@gss/types/Dimension";
import type {
    FilterOption,
    MeasurementStat,
    OrgStructure,
} from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type { DimensionScore, QuestionScore } from "@gss/types/statScore";
import { DAO } from "../DAO";
import { combineFilterNames } from "../filters/FilterUtil";
import type { MeasurementStatWithLockedFilters } from "../types";
import { DistinctColors } from "../util/DistinctColors";
import { useTranslation } from "../util/Translation";
import type { Region, SuperGraphData, SuperGraphRef, Unit } from "./SuperGraph";
import { SuperGraph } from "./SuperGraph";

interface Props {
    stat: MeasurementStatWithLockedFilters;
    audience: SecurityContext;
    regionRoles: string[];
}

type GetDataCallback<T> = (dimStats: T) => Record<string, number>;

export function SuperGraphTab({
    stat,
    audience,
    regionRoles,
}: Props): JSX.Element | null {
    const { translation, getTranslation, translateDimension } =
        useTranslation();
    const tS = translation.stats;
    const tF = translation.filters;
    const dimensions = useConst(() =>
        stat.dimensions.filter((d) => d.hasScores).map((d) => d.name),
    );
    const [selectedDimension, setSelectedDimension] =
        useState<DimensionValue>();
    const [selectedQuestion, setSelectedQuestion] = useState<string>();
    const [dimensionData, setDimensionData] = useState<SuperGraphData>();
    const [questionData, setQuestionData] = useState<SuperGraphData>();
    const [questions, setQuestions] = useState<string[]>();
    const [showMyRegionButton, setShowMyRegionButton] = useState<boolean>();
    const countyColors = useConst(() => new DistinctColors());
    const dimensionsGraphRef = useRef<SuperGraphRef>(null);
    const questionsGraphRef = useRef<SuperGraphRef>(null);
    const { addError } = useMessages();
    const orgStructure = getOrgStructure(stat, audience);
    const textSelectAll = tF.selectAll;
    const textSelectNone = tF.selectNone;
    const textSelectMyRegion = tF.selectMyRegion;

    useEffect(() => {
        DAO.stats
            .getDimensionScores(
                stat.securityContext,
                stat.measurement,
                orgStructure.value,
                stat.periods,
            )
            .then((dimensionScores) => {
                // oxlint-disable-next-line react/immutability
                const data = getData(dimensionScores, getDimensionMap);
                setSelectedDimension(dimensions[0]);
                setDimensionData(data);
                setSelectedQuestion(undefined);
                setQuestionData(undefined);
                setQuestions(undefined);
                setShowMyRegionButton(
                    data.regions.some((r) => regionRoles.includes(r.orgId)),
                );
            })
            .catch(addError);
    }, []);

    const setVisibility = (isDimension: boolean, state: boolean | string[]) => {
        if (isDimension) {
            dimensionsGraphRef.current?.setVisibility(state);
        } else {
            questionsGraphRef.current?.setVisibility(state);
        }
    };

    const getData = <T extends { orgIds?: string }>(
        stats: T[],
        getDataCallback: GetDataCallback<T>,
    ): SuperGraphData => {
        const orgTree = new OrgTree(
            stats,
            (stat) => {
                const org = orgStructure.organisations.find(
                    (o) => o.id === stat.orgIds,
                );
                if (org == null) {
                    throw new Error(
                        `Missing organisation for stat orgId: ${stat.orgIds}`,
                    );
                }
                return org.dn;
            },
            (stat) => stat,
        );
        const country = getDimStatNode(getDataCallback, orgTree.root);
        const regions = orgTree.root.children.map((region): Region => {
            const regionData = getDimStatNode(getDataCallback, region);
            const leafNodes = OrgTree.getLeafNodes(region);
            const orgId = region.data?.orgIds;
            if (orgId == null) {
                throw new Error(
                    `Missing organisation id for region: ${region.dn}`,
                );
            }
            return {
                ...regionData,
                orgId,
                units: leafNodes.map((node) =>
                    getDimStatNode(getDataCallback, node),
                ),
            };
        });
        const keys = Object.keys(country.data);
        return { country, regions, keys };
    };

    if (selectedDimension == null) {
        return null;
    }

    const fetchQuestions = () => {
        DAO.stats
            .getQuestionScores(
                stat.securityContext,
                stat.measurement,
                orgStructure.value,
                stat.periods,
            )
            .then((questionScores) => {
                const data = getData(questionScores, getQuestionMap);
                setQuestions(data.keys);
                setSelectedQuestion(data.keys[0]);
                setQuestionData(data);
            })
            .catch(addError);
    };

    const selectDimension = (dimension: DimensionValue | undefined) => {
        setSelectedDimension(dimension);
        setQuestions(undefined);
        setQuestionData(undefined);
    };

    const selectQuestion = (question: string | undefined) => {
        setSelectedQuestion(question);
    };

    const callbacks = {
        getTitle: (value: string) => {
            return {
                title: value,
                subtitle: getSubTitle(
                    stat.measurement,
                    stat.periods,
                    stat.lockedFilters,
                ),
            };
        },
        getCountyColor: (allNames: string[], name: string) =>
            countyColors.getMap(allNames)[name],
    };

    const renderButtons = (isDimension: boolean) => {
        return (
            <span className="float-end">
                <Button
                    variant="default"
                    className="me-1"
                    onClick={() => setVisibility(isDimension, true)}
                >
                    {textSelectAll}
                </Button>
                <Button
                    variant="default"
                    className="me-1"
                    onClick={() => setVisibility(isDimension, false)}
                >
                    {textSelectNone}
                </Button>
                {showMyRegionButton && (
                    <Button
                        variant="default"
                        onClick={() => setVisibility(isDimension, regionRoles)}
                    >
                        {textSelectMyRegion}
                    </Button>
                )}
            </span>
        );
    };

    const getDimensionGraph = () => {
        if (dimensionData == null) {
            return null;
        }
        return (
            <div>
                <div className="mb-2">
                    <Select
                        className="d-inline w-auto"
                        onChange={selectDimension}
                        value={selectedDimension}
                    >
                        {dimensions.map((d) => ({
                            value: d,
                            children: translateDimension(d),
                        }))}
                    </Select>
                    {renderButtons(true)}
                </div>

                <SuperGraph
                    ref={dimensionsGraphRef}
                    callbacks={callbacks}
                    data={dimensionData}
                    value={selectedDimension}
                    name={translateDimension(selectedDimension)}
                    axisTitle={tS.dimensionScoreYAxis}
                />
            </div>
        );
    };

    const getQuestions = () => {
        if (dimensionData == null) {
            return null;
        }
        return (
            <div>
                <hr />
                {getGetQuestionsButton()}
                {getQuestionsGraph()}
            </div>
        );
    };

    const getGetQuestionsButton = () => {
        if (questions) {
            return null;
        }
        return (
            <Button variant="primary" onClick={fetchQuestions} className="mb-2">
                {tS.showQuestions}
            </Button>
        );
    };

    const getQuestionText = (qId: string): string => {
        const question = stat.questions.find((q) => q.id === qId);
        return (
            (question != null ? getTranslation(question.texts) : null) ?? qId
        );
    };

    const getQuestionsGraph = () => {
        if (
            questions == null ||
            questionData == null ||
            selectedQuestion == null
        ) {
            return null;
        }
        return (
            <div>
                <div className="mb-2">
                    <Select
                        className="w-auto"
                        onChange={selectQuestion}
                        value={selectedQuestion}
                    >
                        {questions.map((qId) => ({
                            value: qId,
                            children: getQuestionText(qId),
                        }))}
                    </Select>
                    {renderButtons(false)}
                </div>

                <SuperGraph
                    ref={questionsGraphRef}
                    callbacks={callbacks}
                    data={questionData}
                    value={selectedQuestion}
                    name={getQuestionText(selectedQuestion)}
                    axisTitle={tS.meanValueYAxis}
                />
            </div>
        );
    };

    if (dimensionData == null) {
        return null;
    }
    return (
        <div>
            {getDimensionGraph()}
            {getQuestions()}
        </div>
    );
}

function getDimStatNode<T extends { orgIds?: string }>(
    getDataCallback: GetDataCallback<T>,
    node: TreeNode<T>,
): Unit {
    const org = node.data;
    if (org == null) {
        throw new Error(`Tree node has no organisation`);
    }
    return {
        name: new DN(node.dn).getLastValue(),
        orgId: org.orgIds,
        data: getDataCallback(org),
    };
}

function getOrgStructure(
    stat: MeasurementStat,
    audience: SecurityContext,
): OrgStructure {
    const orgStructure = (() => {
        const internalStruc = stat.orgStructures.find(
            (o) => o.value === "internal",
        );
        if (audience === "authenticated") {
            return internalStruc;
        }
        const pubStruc = stat.orgStructures.find(
            (o) => o.value === "public" && o.organisations.length > 1,
        );
        return pubStruc ?? internalStruc;
    })();
    if (orgStructure == null) {
        throw new Error(`Can't find organisation structure`);
    }
    return orgStructure;
}

function getSubTitle(
    measurement: string,
    periods: string,
    lockedFilters: Record<string, FilterOption>,
): string {
    const filterValues: string[] = [measurement, periods];
    for (const i of Object.keys(lockedFilters)) {
        filterValues.push(lockedFilters[i].name);
    }
    return combineFilterNames(filterValues);
}

function getDimensionMap(dimScore: DimensionScore): Record<string, number> {
    const res: Record<string, number> = {};
    if (dimScore.status === "ok") {
        for (const dim of dimScore.dimensions) {
            res[dim.name] = dim.score;
        }
    }
    return res;
}

function getQuestionMap(questionScore: QuestionScore): Record<string, number> {
    const res: Record<string, number> = {};
    for (const q of questionScore.questions) {
        if (q.status === "ok") {
            res[q.id] = q.score.value;
        }
    }
    return res;
}
